/**
 * Wallet Transactions History Hook
 * Handles fetching wallet transaction history with pagination
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

/**
 * Hook to fetch wallet transaction history
 * @param {number} userId - User ID
 * @param {number} walletId - Wallet ID
 * @param {Object} options - Additional options
 * @returns {Object} Query result with transactions data
 */
export const useWalletTransactionsHistory = (
  userId,
  walletId,
  options = {},
) => {
  const { enabled = true, page = 1, pageSize = 20, ...queryOptions } = options;

  return useQuery({
    queryKey: ["wallet-transactions", userId, walletId, page, pageSize],
    queryFn: async () => {
      if (!userId || !walletId) return null;

      try {
        const params = new URLSearchParams({
          orderBy: "-id",
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        const response = await apiClient.get(
          `/users/${userId}/wallets/${walletId}/events?${params.toString()}`,
        );

        return {
          transactions: response.result || [],
          pagination: response.pagination || {},
        };
      } catch (err) {
        throw new Error(
          err.response?.data?.message || "Failed to fetch wallet transactions",
        );
      }
    },
    enabled:
      enabled &&
      !!userId &&
      !!walletId &&
      isEndpointEnabled("dashboard", "users"),
    staleTime: 30000, // 30 seconds
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    ...queryOptions,
  });
};

/**
 * Parse wallet transaction event for display
 * @param {Object} transaction - Transaction object
 * @param {Array} users - Array of users for author lookup
 * @returns {Object} Parsed event information
 */
export const parseWalletEvent = (transaction, users = []) => {
  const { event, event_type, amount, author_id, description, event_data } =
    transaction;

  let eventLog = "";
  let eventIcon = "Activity";
  let eventColor = "text-gray-400";

  // Find author
  const author = users.find((user) => user.id === author_id);
  const authorName = author
    ? `${author.name} ${author.lastname}`
    : `User ${author_id}`;

  // Parse event based on type
  switch (event_type) {
    case "manual":
      switch (event) {
        case "movement":
          if (Math.sign(amount) === -1) {
            eventLog = `${authorName} deducted money`;
            eventIcon = "Minus";
            eventColor = "text-red-400";
          } else {
            eventLog = `${authorName} added money`;
            eventIcon = "Plus";
            eventColor = "text-green-400";
          }
          break;
        case "locked":
          eventLog = `${authorName} locked wallet`;
          eventIcon = "Lock";
          eventColor = "text-red-400";
          break;
        case "unlocked":
          eventLog = `${authorName} unlocked wallet`;
          eventIcon = "Unlock";
          eventColor = "text-green-400";
          break;
        default:
          eventLog = event;
          break;
      }
      break;

    case "redeem_gift_card":
      eventLog = "Redeemed from Gift Card";
      eventIcon = "Gift";
      eventColor = "text-purple-400";
      break;

    case "refund":
      eventLog = "Refund processed";
      eventIcon = "RotateCcw";
      eventColor = "text-blue-400";
      break;

    case "payment":
      eventLog = "Payment made";
      eventIcon = "CreditCard";
      eventColor = "text-orange-400";
      break;

    case "accumulation_credit_point":
      eventLog = "Credit points earned";
      eventIcon = "Star";
      eventColor = "text-yellow-400";
      break;

    default:
      eventLog = event_type || event;
      break;
  }

  return {
    eventLog,
    eventIcon,
    eventColor,
    authorName,
    description: description || "",
    orderId: event_data?.order_id || event_data?.cart_id || null,
    orderUuids: event_data?.uuids || null,
  };
};
